import { getRaceEthnicityDisplayLabel } from "./childProfileOptions";
import { getEducationLevelLabel, getHearAboutUsDisplayLabel } from "./parentProfileOptions";
import { getPhotographyConsentLabel, getPhotographyConsentSortKey } from "./photographyConsent";
import { displayEmailOrDash } from "./displayEmail";
import { formatPhoneDisplay } from "./phoneFormat";
import { formatBirthMonthYearDisplay } from "./birthMonthYear";
import { getGenderDisplayLabel } from "./genderOptions";
import { getGradeLabel } from "./gradeOptions";

export const PARTICIPANT_PROFILE_DETAILS_TABLE_COLUMNS = [
  { id: "participant", label: "Participant" },
  { id: "preferred_name", label: "Preferred name" },
  { id: "birthday", label: "Date of birth" },
  { id: "gender", label: "Gender" },
  { id: "grade", label: "Grade" },
  { id: "school", label: "School" },
  { id: "school_district", label: "School district" },
  { id: "race_ethnicity", label: "Race / Ethnicity" },
  { id: "photo_consent", label: "Photo consent" },
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone" },
  { id: "parent_name", label: "Parent / Guardian" },
  { id: "parent_preferred_name", label: "Parent preferred name" },
  { id: "parent_gender", label: "Parent gender" },
  { id: "parent_zip_code", label: "Zip code" },
  { id: "parent_highest_education", label: "Highest education" },
  { id: "parent_hear_about_us", label: "How did you hear about us" },
  { id: "parent_email", label: "Parent email" },
  { id: "parent_phone", label: "Parent phone" },
];

export const PARTICIPANT_PROFILE_DETAILS_DEFAULT_VISIBLE = [
  "participant",
  "preferred_name",
  "grade",
  "school_district",
  "race_ethnicity",
  "photo_consent",
  "parent_preferred_name",
  "parent_zip_code",
  "parent_email",
];

export const PARTICIPANT_PROFILE_DETAILS_TABLE_KEY = "event_participant_details.table";

function formatPersonName(first, last) {
  const parts = [first, last]
    .map((x) => (x != null ? String(x).trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(" ") : "";
}

function textOrDash(raw) {
  return raw != null && String(raw).trim() ? String(raw).trim() : "—";
}

export function formatParticipantProfileName(row) {
  return formatPersonName(row?.first_name, row?.last_name) || "—";
}

/** Display string for a column (search + table cells that are plain text). */
export function getParticipantProfileDetailsDisplay(row, colId) {
  switch (colId) {
    case "participant":
      return formatParticipantProfileName(row);
    case "preferred_name":
      return textOrDash(row?.preferred_name);
    case "birthday": {
      const label = formatBirthMonthYearDisplay(row?.birthday);
      return label || "—";
    }
    case "gender":
      return getGenderDisplayLabel(row?.gender) || "—";
    case "grade":
      return getGradeLabel(row?.grade) || "—";
    case "school":
      return textOrDash(row?.school);
    case "school_district":
      return textOrDash(row?.school_district);
    case "race_ethnicity":
      return getRaceEthnicityDisplayLabel(row?.race_ethnicity, row?.race_ethnicity_other);
    case "photo_consent":
      return getPhotographyConsentLabel(row?.photography_consent);
    case "email":
      return displayEmailOrDash(row?.email);
    case "phone":
      return row?.phone != null && String(row.phone).trim()
        ? formatPhoneDisplay(row.phone)
        : "—";
    case "parent_name":
      return row?.parent_name != null && String(row.parent_name).trim()
        ? String(row.parent_name).trim()
        : formatPersonName(row?.parent_first_name, row?.parent_last_name) || "—";
    case "parent_preferred_name":
      return textOrDash(row?.parent_preferred_name);
    case "parent_gender":
      return getGenderDisplayLabel(row?.parent_gender) || "—";
    case "parent_zip_code":
      return textOrDash(row?.parent_zip_code);
    case "parent_highest_education":
      return getEducationLevelLabel(row?.parent_highest_education_level);
    case "parent_hear_about_us":
      return getHearAboutUsDisplayLabel(row?.parent_hear_about_us, row?.parent_hear_about_us_other);
    case "parent_email":
      return displayEmailOrDash(row?.parent_email);
    case "parent_phone":
      return row?.parent_phone != null && String(row.parent_phone).trim()
        ? formatPhoneDisplay(row.parent_phone)
        : "—";
    default:
      return row?.[colId] != null ? String(row[colId]) : "—";
  }
}

/** Sort value for a column (raw / comparable). */
export function getParticipantProfileDetailsSortValue(row, colId) {
  switch (colId) {
    case "participant":
      return formatPersonName(row?.first_name, row?.last_name).toLowerCase();
    case "photo_consent":
      return getPhotographyConsentSortKey(row?.photography_consent);
    case "email":
      return row?.email != null ? String(row.email).toLowerCase() : "";
    case "parent_email":
      return row?.parent_email != null ? String(row.parent_email).toLowerCase() : "";
    case "parent_highest_education":
      return getEducationLevelLabel(row?.parent_highest_education_level);
    case "parent_hear_about_us":
      return getHearAboutUsDisplayLabel(row?.parent_hear_about_us, row?.parent_hear_about_us_other);
    case "race_ethnicity":
      return getRaceEthnicityDisplayLabel(row?.race_ethnicity, row?.race_ethnicity_other);
    default: {
      const display = getParticipantProfileDetailsDisplay(row, colId);
      return display === "—" ? "" : String(display).toLowerCase();
    }
  }
}

export function matchesParticipantProfileDetailsSearch(row, searchTerm) {
  const q = (searchTerm || "").trim().toLowerCase();
  if (!q) return true;
  return PARTICIPANT_PROFILE_DETAILS_TABLE_COLUMNS.some((col) =>
    String(getParticipantProfileDetailsDisplay(row, col.id) || "")
      .toLowerCase()
      .includes(q)
  );
}
